import { supabase } from './supabase';

export const DEFAULT_AREAS = ['Limpieza', 'Cocina', 'Docente', 'Administración', 'Mantenimiento', 'Seguridad', 'Otro'];

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// Perfiles de pago: cada uno es una categoría (área) con su valor por hora y viático.
export const getHourlyConfig = async () => {
  const { data } = await supabase.from('org_hourly_config').select('*').maybeSingle();
  let profiles = Array.isArray(data?.profiles) ? data.profiles : null;
  if (!profiles) profiles = (data?.areas?.length ? data.areas : DEFAULT_AREAS).map((name) => ({ name, rate: null, per_diem: null }));
  profiles = profiles.map((p) => ({ name: p.name, rate: num(p.rate), per_diem: num(p.per_diem) }));
  return { default_rate: Number(data?.default_rate) || 0, default_per_diem: Number(data?.default_per_diem) || 0, profiles, areas: profiles.map((p) => p.name) };
};

export const saveHourlyConfig = async (orgId, cfg) => {
  const profiles = cfg.profiles.map((p) => ({ name: p.name.trim(), rate: num(p.rate), per_diem: num(p.per_diem) })).filter((p) => p.name);
  const { error } = await supabase.from('org_hourly_config').upsert({
    organization_id: orgId, default_rate: Number(cfg.default_rate) || 0, default_per_diem: Number(cfg.default_per_diem) || 0,
    profiles, areas: profiles.map((p) => p.name), updated_at: new Date().toISOString(),
  }, { onConflict: 'organization_id' });
  if (error) throw error;
};

// Valor por hora y viático de una persona: propio > de su perfil (área) > general.
export const ratesFor = (cfg, area, c = {}) => {
  const pr = (cfg.profiles || []).find((p) => p.name === area) || {};
  const own = Number(c.hourly_rate) > 0 ? Number(c.hourly_rate) : null;
  const ownD = c.per_diem != null && c.per_diem !== '' ? Number(c.per_diem) : null;
  return {
    rate: own ?? (pr.rate != null ? pr.rate : cfg.default_rate),
    perDiem: ownD ?? (pr.per_diem != null ? pr.per_diem : cfg.default_per_diem),
    from: own != null ? 'propio' : pr.rate != null ? 'perfil' : 'general',
  };
};

export const listSettlements = async ({ employeeId, from, to } = {}) => {
  let q = supabase.from('hourly_settlements').select('*, employee:profiles!hourly_settlements_employee_id_fkey(id,name,email)').order('period_from', { ascending: false });
  if (employeeId) q = q.eq('employee_id', employeeId);
  if (from) q = q.gte('period_to', from);
  if (to) q = q.lte('period_from', to);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
};

export const minutesOf = (r) => {
  if (!r.check_in || !r.check_out) return null;
  if (['absent', 'holiday', 'justified'].includes(r.status)) return 0;
  const m = r.minutes_worked != null ? Number(r.minutes_worked) : Math.round((new Date(r.check_out) - new Date(r.check_in)) / 60000);
  return Math.max(0, m);
};

// Calcula lo que corresponde a cada persona por hora en el período (los minutos se pagan proporcionalmente).
export const computeHourly = async ({ employees, comp, cfg, from, to }) => {
  const ids = employees.map(e => e.id);
  if (!ids.length) return [];
  const [{ data: recs }, { data: sets }, { data: advs }] = await Promise.all([
    supabase.from('attendance_records').select('employee_id,date,check_in,check_out,minutes_worked,status').in('employee_id', ids).gte('date', from).lte('date', to),
    supabase.from('hourly_settlements').select('employee_id,period_from,period_to').in('employee_id', ids).lte('period_from', to).gte('period_to', from),
    supabase.from('salary_advances').select('id,employee_id,amount,paid_on,status').in('employee_id', ids).is('settlement_id', null).in('status', ['approved', 'paid']),
  ]);
  return employees.map(e => {
    const c = comp[e.id] || {};
    const { rate, perDiem } = ratesFor(cfg, e.area, c);
    const covered = (sets || []).filter(s => s.employee_id === e.id);
    const mine = (recs || []).filter(r => r.employee_id === e.id && !covered.some(s => r.date >= s.period_from && r.date <= s.period_to));
    let minutes = 0, incomplete = 0; const days = new Set();
    mine.forEach(r => {
      if (!r.check_in) return;
      const m = minutesOf(r);
      if (m === null) { incomplete++; return; }
      if (m > 0) { minutes += m; days.add(r.date); }
    });
    const hoursAmt = Math.round(minutes / 60 * rate);
    const perDiemAmt = Math.round(days.size * perDiem);
    const myAdv = (advs || []).filter(a => a.employee_id === e.id);
    const advAmt = myAdv.reduce((s, a) => s + Number(a.amount), 0);
    return { emp: e, rate, perDiem, minutes, days: days.size, incomplete, hoursAmt, perDiemAmt, advAmt, advIds: myAdv.map(a => a.id), overlap: covered.length > 0 };
  });
};

export const createSettlement = async ({ row, from, to, bonus, notes, adminId }) => {
  const b = Number(bonus) || 0;
  const total = row.hoursAmt + row.perDiemAmt + b - row.advAmt;
  const { data, error } = await supabase.from('hourly_settlements').insert({
    employee_id: row.emp.id, period_from: from, period_to: to, minutes: row.minutes, hourly_rate: row.rate, hours_amt: row.hoursAmt,
    days: row.days, per_diem: row.perDiem, per_diem_amt: row.perDiemAmt, bonus: b, advances_amt: row.advAmt, total, notes: notes || null, created_by: adminId,
  }).select().single();
  if (error) throw error;
  if (row.advIds.length) {
    const { error: e2 } = await supabase.from('salary_advances').update({ settlement_id: data.id }).in('id', row.advIds);
    if (e2) throw e2;
  }
  return data;
};

export const markSettlementPaid = async (id, adminId, paid = true) => {
  const { error } = await supabase.from('hourly_settlements')
    .update(paid ? { status: 'paid', paid_at: new Date().toISOString(), paid_by: adminId } : { status: 'approved', paid_at: null, paid_by: null }).eq('id', id);
  if (error) throw error;
};
